import { ArrowDropDown, ExpandMore } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, Checkbox, CircularProgress, FormControlLabel, Grid, IconButton, Menu, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material'
import AngleRightIcon from 'common/customIcons/angle_right'
import MDButton from 'components/MDButton'
import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { daysOfWeek } from 'utilities/common'
import { useDispatch } from 'react-redux'
import { fetchDriversRegions } from '../../../../redux/slices/hyperwolf/driver/driverRegion'
import { openSnackbar } from '../../../../redux/slices/common'
import { CreateShopTiming } from '../../../../redux/slices/hyperwolf/settings'
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { TimePickerASAP } from '../TimePickerASAP'

const CreateGeneralTiming = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [formData, setFormData] = useState({
        days: [] // { day: string, slots: [...], isClosed: boolean, regionOverrides: [...] }
    });
    const { isUploading = false } = useSelector((state => state.common));
    const [anchorEl, setAnchorEl] = useState(null);
    const [repeatSourceDay, setRepeatSourceDay] = useState(null);
    const [selectedRepeatDays, setSelectedRepeatDays] = useState([]); // Track selected days for blue ticks
    const { isLoading } = useSelector(state => state.hyperwolfSettings);
    const [validationErrors, setValidationErrors] = useState({});
    
    const [regionData, setRegionData] = useState([]);
    const { user } = useSelector(state => state.auth);
    // Get all selected region IDs across all days and all region overrides
    const getAllSelectedRegionIds = () => {
        return formData.days.flatMap(day =>
            day.regionOverrides.flatMap(region => region.regionId || [])
        );
    };

    // Check if all regions are used across all days
    const areAllRegionsUsed = () => {
        const allSelectedIds = getAllSelectedRegionIds();
        return regionData.length > 0 && allSelectedIds.length >= regionData.length;
    };

    // Get available regions (not selected in any day)
    const getAvailableRegions = () => {
        const allSelectedIds = getAllSelectedRegionIds();
        return regionData.filter(region => !allSelectedIds.includes(region.id));
    };

    // Check if a region is disabled (already selected in another day or region override)
    const isRegionDisabled = (regionId, currentDay, currentRegionIndex) => {
        const allSelectedIds = getAllSelectedRegionIds();
        const currentRegionIds = formData.days
            .find(day => day.day === currentDay)
            ?.regionOverrides[currentRegionIndex]?.regionId || [];

        const isSelectedElsewhere = allSelectedIds.includes(regionId) &&
            !currentRegionIds.includes(regionId);

        return isSelectedElsewhere;
    };
    const openRepeatMenu = (e, day) => {
        setAnchorEl(e.currentTarget);
        setRepeatSourceDay(day);
        // Reset selected repeat days when opening menu for a new source day
        setSelectedRepeatDays([]);
    };

    const closeRepeatMenu = () => {
        setAnchorEl(null);
        setRepeatSourceDay(null);
        setSelectedRepeatDays([]);
    };

    const validateForm = () => {
        const errors = {};
        let hasErrors = false;

        formData.days.forEach(dayObj => {
            if (dayObj.isClosed) return; // Skip validation for closed days

            // Validate main time slots
            dayObj.slots.forEach((slot, slotIndex) => {
                // Validate open time
                if (!slot.startTime) {
                    if (!errors[dayObj.day]) errors[dayObj.day] = {};
                    if (!errors[dayObj.day][slotIndex]) errors[dayObj.day][slotIndex] = {};
                    errors[dayObj.day][slotIndex].startTime = 'Enter a valid open time (eg: 9:45 AM)';
                    hasErrors = true;
                }

                // Validate close time
                if (!slot.endTime) {
                    if (!errors[dayObj.day]) errors[dayObj.day] = {};
                    if (!errors[dayObj.day][slotIndex]) errors[dayObj.day][slotIndex] = {};
                    errors[dayObj.day][slotIndex].endTime = 'Enter a valid close time (eg: 8:45 PM)';
                    hasErrors = true;
                }

                // Validate time logic (close time should be after open time)
                if (slot.startTime && slot.endTime) {
                    const openMinutes = parseTimeToMinutes(slot.startTime);
                    const closeMinutes = parseTimeToMinutes(slot.endTime);

                    if (closeMinutes <= openMinutes) {
                        if (!errors[dayObj.day]) errors[dayObj.day] = {};
                        if (!errors[dayObj.day][slotIndex]) errors[dayObj.day][slotIndex] = {};
                        errors[dayObj.day][slotIndex].endTime = 'Close time must be after open time';
                        hasErrors = true;
                    }
                }
            });

            // Validate region overrides only if override is enabled
            dayObj.regionOverrides.forEach((region, regionIndex) => {
            
                    // Validate region selection
                    if (!region.regionId || region.regionId.length === 0) {
                        if (!errors[dayObj.day]) errors[dayObj.day] = {};
                        if (!errors[dayObj.day].regions) errors[dayObj.day].regions = {};
                        errors[dayObj.day].regions[regionIndex] = {
                            ...errors[dayObj.day].regions?.[regionIndex],
                            regionId: 'Region selection is required'
                        };
                        hasErrors = true;
                    }

                    // Validate open time
                    if (!region.openTime) {
                        if (!errors[dayObj.day]) errors[dayObj.day] = {};
                        if (!errors[dayObj.day].regions) errors[dayObj.day].regions = {};
                        errors[dayObj.day].regions[regionIndex] = {
                            ...errors[dayObj.day].regions?.[regionIndex],
                            openTime: 'Enter a valid open time (eg: 9:45 AM)'
                        };
                        hasErrors = true;
                    }

                    // Validate close time
                    if (!region.closeTime) {
                        if (!errors[dayObj.day]) errors[dayObj.day] = {};
                        if (!errors[dayObj.day].regions) errors[dayObj.day].regions = {};
                        errors[dayObj.day].regions[regionIndex] = {
                            ...errors[dayObj.day].regions?.[regionIndex],
                            closeTime: 'Enter a valid close time (eg: 8:45 PM)'
                        };
                        hasErrors = true;
                    }

                    // Validate time logic (close time should be after open time)
                    if (region.openTime && region.closeTime) {
                        const openMinutes = parseTimeToMinutes(region.openTime);
                        const closeMinutes = parseTimeToMinutes(region.closeTime);

                        if (closeMinutes <= openMinutes) {
                            if (!errors[dayObj.day]) errors[dayObj.day] = {};
                            if (!errors[dayObj.day].regions) errors[dayObj.day].regions = {};
                            errors[dayObj.day].regions[regionIndex] = {
                                ...errors[dayObj.day].regions?.[regionIndex],
                                closeTime: 'Close time must be after open time'
                            };
                            hasErrors = true;
                        }
                    }
                
            });
        });

        setValidationErrors(errors);
        return !hasErrors;
    };

    const handleDayToggle = (day) => {
        setFormData((prev) => {
            const exists = prev.days.find((d) => d?.day === day);
            if (exists) {
                return {
                    ...prev,
                    days: prev.days.filter((d) => d?.day !== day),
                };
            } else {
                return {
                    ...prev,
                    days: [
                        ...prev.days,
                        {
                            day,
                            isClosed: false,
                            slots: [{ startTime: "", endTime: "" }],
                            regionOverrides: [
                            ]
                        }
                    ],
                };
            }
        });
    };

    const handleSlotChange = (day, slotIndex, field, value) => {
        setFormData(prev => ({
            ...prev,
            days: prev.days.map(d => {
                if (d.day === day) {
                    const updatedSlots = d.slots.map((slot, index) =>
                        index === slotIndex ? { ...slot, [field]: value } : slot
                    );

                    let updatedRegionOverrides = d.regionOverrides;
                    // Only auto-fill region times if they are empty
                    if (field === 'startTime') {
                        updatedRegionOverrides = d.regionOverrides.map(region => ({
                            ...region,
                            openTime: region.openTime === "" ? value : region.openTime
                        }));
                    } else if (field === 'endTime') {
                        updatedRegionOverrides = d.regionOverrides.map(region => ({
                            ...region,
                            closeTime: region.closeTime === "" ? value : region.closeTime
                        }));
                    }

                    return {
                        ...d,
                        slots: updatedSlots,
                        regionOverrides: updatedRegionOverrides
                    };
                }
                return d;
            })
        }));

        // Clear validation errors when user starts typing
        if (validationErrors[day]?.[slotIndex]?.[field]) {
            setValidationErrors(prev => ({
                ...prev,
                [day]: {
                    ...prev[day],
                    [slotIndex]: {
                        ...prev[day]?.[slotIndex],
                        [field]: ''
                    }
                }
            }));
        }
    };

    const handleRegionOverrideChange = (day, regionIndex, field, value) => {
        setFormData(prev => ({
            ...prev,
            days: prev.days.map(d => {
                if (d.day === day) {
                    const updatedRegionOverrides = d.regionOverrides.map((region, index) => {
                        if (index === regionIndex) {
                            if (field === 'regionId') {
                                let newRegionIds;

                                // Check if Select All was clicked
                                const isSelectAllClicked = value.includes('select-all') ||
                                    (Array.isArray(value) && value.find(val => val === 'select-all'));

                                if (isSelectAllClicked) {
                                    // Select all available regions
                                    const availableRegions = getAvailableRegions();
                                    const currentRegionIds = region.regionId || [];
                                    // Combine currently selected with available regions
                                    newRegionIds = [...new Set([...currentRegionIds, ...availableRegions.map(reg => reg.id)])];
                                } else {
                                    // Regular selection - filter out already selected regions from other days
                                    const availableRegions = getAvailableRegions();
                                    const currentRegionIds = region.regionId || [];
                                    const selectedIds = Array.isArray(value) ? value.filter(val => val !== 'select-all') : [value].filter(Boolean);

                                    // Only allow regions that are either already in this override or available
                                    newRegionIds = selectedIds.filter(regionId =>
                                        currentRegionIds.includes(regionId) ||
                                        availableRegions.some(reg => reg.id === regionId)
                                    );
                                }

                                return {
                                    ...region,
                                    regionId: newRegionIds
                                };
                            }
                            return {
                                ...region,
                                [field]: value
                            };
                        }
                        return region;
                    });

                    return {
                        ...d,
                        regionOverrides: updatedRegionOverrides
                    };
                }
                return d;
            })
        }));

        // Clear validation errors
        if (validationErrors[day]?.regions?.[regionIndex]?.[field]) {
            setValidationErrors(prev => ({
                ...prev,
                [day]: {
                    ...prev[day],
                    regions: {
                        ...prev[day]?.regions,
                        [regionIndex]: {
                            ...prev[day]?.regions?.[regionIndex],
                            [field]: ''
                        }
                    }
                }
            }));
        }
    };

    const handleRegionClosedToggle = (day, regionIndex) => {
        setFormData(prev => ({
            ...prev,
            days: prev.days.map(d =>
                d.day === day ? {
                    ...d,
                    regionOverrides: d.regionOverrides.map((region, index) =>
                        index === regionIndex ? { ...region, isClosed: !region.isClosed } : region
                    )
                } : d
            )
        }));
    };

    const addRegionOverride = (day) => {
        // Don't add new region override if all regions are already used
        if (areAllRegionsUsed()) {
            showError('All regions have been assigned. You cannot add more region overrides.', 'error');
            return;
        }

        setFormData(prev => ({
            ...prev,
            days: prev.days.map(d => {
                if (d.day === day) {
                    const newRegion = {
                        regionId: "",
                        openTime: "",
                        closeTime: "",
                        isClosed: true
                    };

                    if (d.slots.length > 0) {
                        const mainSlot = d.slots[0];
                        newRegion.openTime = mainSlot.startTime;
                        newRegion.closeTime = mainSlot.endTime;
                    }

                    return {
                        ...d,
                        regionOverrides: [...d.regionOverrides, newRegion]
                    };
                }
                return d;
            })
        }));
    };

    const removeRegionOverride = (day, regionIndex) => {
        setFormData(prev => ({
            ...prev,
            days: prev.days.map(d =>
                d.day === day ? {
                    ...d,
                    regionOverrides: d.regionOverrides.filter((_, index) => index !== regionIndex)
                } : d
            )
        }));

        // Clear validation errors for removed region
        if (validationErrors[day]?.regions?.[regionIndex]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                if (newErrors[day]?.regions) {
                    delete newErrors[day].regions[regionIndex];
                    // Clean up empty objects
                    if (Object.keys(newErrors[day].regions).length === 0) {
                        delete newErrors[day].regions;
                    }
                    if (Object.keys(newErrors[day]).length === 0) {
                        delete newErrors[day];
                    }
                }
                return newErrors;
            });
        }
    };

    const handleRepeatSlots = (sourceDay, targetDays) => {
        const sourceDayData = formData.days.find(d => d.day === sourceDay);
        if (!sourceDayData) return;

        setFormData(prev => ({
            ...prev,
            days: prev.days.map(d =>
                targetDays.includes(d.day) ? { ...sourceDayData, day: d.day } : d
            )
        }));

        // Update selected repeat days for blue ticks
        setSelectedRepeatDays(prev => {
            const newSelected = [...prev];
            targetDays.forEach(day => {
                if (!newSelected.includes(day)) {
                    newSelected.push(day);
                }
            });
            return newSelected;
        });

        // closeRepeatMenu();
    };

    const handleRepeatDayToggle = (day) => {
        const isSelected = selectedRepeatDays.includes(day);

        if (isSelected) {
            // Remove from selected
            setSelectedRepeatDays(prev => prev.filter(d => d !== day));
        } else {
            // Add to selected and apply
            setSelectedRepeatDays(prev => [...prev, day]);
            handleRepeatSlots(repeatSourceDay, [day]);
        }
    };

    const showError = (message, errortype) => {
        if (errortype === 'success') {
            dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
        }
        if (errortype === 'error') {
            dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
        }
    };

    const handleSubmit = () => {
        if (!validateForm()) {
            showError(`Please fill all required fields before submitting.`, 'error');
            return;
        }
        // Prepare payload - group by day with slots array
        const payload = formData.days.map(dayData => {

            if (dayData.isClosed) {
                return {
                    updatedBy: user?.name,
                    day: dayData.day,
                    slots: [] // Empty slots for closed days
                };
            }
            // Rename main slots to defaultTime
            // Take the first slot as defaultTime object
            const defaultTime = dayData.slots[0]
                ? { startTime: dayData.slots[0].startTime, endTime: dayData.slots[0].endTime }
                : null;
            // Create slots from region overrides
            const slots = dayData.regionOverrides
                // .filter(region => !region.isClosed && region.regionId && region.regionId.length > 0)
                .flatMap(region =>
                    region.regionId.map(regionId => ({
                        openTime: region.openTime,
                        closeTime: region.closeTime,
                        regionId: regionId,
                        isClosed: region.isClosed ? false : true
                    }))
                );

            return {
                updatedBy: user?.name,
                day: dayData.day,
                slots: slots,
                defaultTime
            };
        });

        // Here you would typically make an API call
        dispatch(CreateShopTiming(payload)).unwrap().then(res => {
            if (res?.message) {
                navigate('/hyperwolf/asap-time-slot?roleTab=generalTimings')
            }
            showError(res?.message, 'success');
        }).catch((err) => {
            showError(err?.message, 'error');
        });
    };

    const parseTimeToMinutes = (timeString) => {
        if (!timeString) return 0;
        const [hours, minutes] = timeString.split(':').map(Number);
        return hours * 60 + minutes;
    };

    const hasSlotError = (day, slotIndex) => {
        return validationErrors[day] && validationErrors[day][slotIndex] &&
            Object.values(validationErrors[day][slotIndex]).some(error => error);
    };

    const hasRegionError = (day, regionIndex) => {
        return validationErrors[day]?.regions?.[regionIndex] &&
            Object.values(validationErrors[day].regions[regionIndex]).some(error => error);
    };

    const selectedDaysList = formData?.days.map(d => d.day);

    useEffect(() => {
        dispatch(fetchDriversRegions())
            .unwrap()
            .then((res) => {
                setRegionData(res?.data.regions || []);
            });
    }, [])

    return (
        <Box p={{ xs: 2, sm: 3, lg: 4 }} pb={{ lg: 2 }} className="create-time-slots">
            <Stack direction="row" justifyContent="space-between" flexWrap="wrap" alignItems="stretch" gap={2} mb={2}>
                <span onClick={() => navigate('/hyperwolf/asap-time-slot?roleTab=generalTimings')} className='cur-pointer'>
                    <Box display="flex" gap={1} alignItems="center" color='text.main' lineHeight={1}>
                        <AngleRightIcon fontSize="14px" />
                        <Typography variant="h6" fontWeight="semibold" ml={0.75}>
                            Delivery hours
                        </Typography>
                    </Box>
                </span>
            </Stack>
            <Box p={{ xs: 0 }}>
                <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: 'none' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={12} lg={12}>
                            <Typography variant='h5' fontWeight="semibold" color="secondary">Add Delivery Hours</Typography>
                        </Grid>

                        <Grid item xs={12} md={12} lg={12}>
                            <Typography variant='h6' fontWeight="semibold" color="#2E2E3A" mb={0.5}>Select Days</Typography>
                            <Stack direction="row" flexWrap="wrap" alignItems="stretch" gap={2} mb={2} className="select-days-sec">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.days.length === daysOfWeek.length}
                                            indeterminate={
                                                formData.days.length > 0 &&
                                                formData.days.length < daysOfWeek.length
                                            }
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    const allDays = daysOfWeek.map(day => ({
                                                        day,
                                                        isClosed: false,
                                                        slots: [{ startTime: "", endTime: "" }],
                                                        regionOverrides: [
                                                          
                                                        ]
                                                    }));
                                                    setFormData({ days: allDays });
                                                } else {
                                                    setFormData({ days: [] });
                                                }
                                                setValidationErrors({});
                                            }}
                                        />
                                    }
                                    label={formData.days.length === daysOfWeek.length ? "Select All" : "Select All"}
                                />

                                {daysOfWeek?.map((day) => (
                                    <FormControlLabel
                                        key={day}
                                        control={
                                            <Checkbox
                                                checked={formData.days.some((d) => d.day === day)}
                                                onChange={() => handleDayToggle(day)}
                                            />
                                        }
                                        label={day}
                                    />
                                ))}
                            </Stack>
                        </Grid>

                        {formData?.days
                            .slice()
                            .sort((a, b) => daysOfWeek.indexOf(a.day) - daysOfWeek.indexOf(b.day))
                            .map((dayObj) => (
                                <Grid item xs={12} md={12} lg={12} key={dayObj.day}>
                                    <Accordion
                                        sx={{
                                            borderRadius: 2.5,
                                            boxShadow: 'none',
                                            background: "#f5f5ff99",
                                            '&:before': { display: 'none' }
                                        }}
                                        className="shop-timing-accordion"
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMore />}
                                            aria-controls={`${dayObj.day}-content`}
                                            id={`${dayObj.day}-header`}
                                            sx={{
                                                '& .MuiAccordionSummary-content': {
                                                    alignItems: 'center',
                                                    gap: '20px'
                                                }
                                            }}
                                        >
                                            <Typography variant="h6" color="#2E2E3A">
                                                {dayObj?.day}
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                style={{ textTransform: "none", color: "#0163FF" }}
                                                endIcon={<ArrowDropDown />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openRepeatMenu(e, dayObj.day);
                                                }}
                                            >
                                                Repeat this slot on
                                            </Button>
                                        </AccordionSummary>

                                        <AccordionDetails>
                                            {!dayObj.isClosed && (
                                                <>
                                                    {/* Main Time Slots */}
                                                    {dayObj?.slots.map((slot, index) => (
                                                        <Box
                                                            key={index}
                                                            className={hasSlotError(dayObj.day, index) ? 'time-sec-rep error-show' : 'time-sec-rep'}
                                                            sx={{
                                                                mb: 3,
                                                                p: 2,
                                                                border: hasSlotError(dayObj.day, index) ? '1px solid #f44336' : '1px solid #e0e0e0',
                                                                borderRadius: 1,
                                                                backgroundColor: hasSlotError(dayObj.day, index) ? '#ffebee' : 'transparent'
                                                            }}
                                                        >
                                                            <Box className="time-card-rep">
                                                                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "start", gap: "5px" }}>
                                                                    <Box sx={{ width: "160px", display: 'flex', alignItems: 'center', flexWrap: 'wrap' }} className="timer-height">
                                                                        <Typography variant='body2' fontWeight="500" mb={0.5} color="#9A9AAF" style={{ width: "100%" }}>Open Time</Typography>
                                                                        <TimePickerASAP
                                                                            value={slot?.startTime}
                                                                            // placeHolder={'Start Time'}
                                                                            onChange={(val) =>
                                                                                handleSlotChange(dayObj?.day, index, "startTime", val)
                                                                            }
                                                                            error={validationErrors[dayObj.day]?.[index]?.startTime}
                                                                        />
                                                                        {(validationErrors[dayObj.day]?.[index]?.startTime || validationErrors[dayObj.day]?.[index]?.endTime) && (
                                                                            <Box sx={{ mt: 0.5 }}>
                                                                                {validationErrors[dayObj.day]?.[index]?.startTime && (
                                                                                    <Typography variant="caption" color="error" display="block" fontWeight="normal">
                                                                                        {validationErrors[dayObj.day][index].startTime}
                                                                                    </Typography>
                                                                                )}
                                                                                {/* {validationErrors[dayObj.day]?.[index]?.endTime && (
                                                                                    <Typography variant="caption" color="error" display="block">
                                                                                        {validationErrors[dayObj.day][index].endTime}
                                                                                    </Typography>
                                                                                )} */}
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                    <span style={{ position: "relative", top: "32px", color: "#2E2E3A", margin: "0 5px" }}>-</span>
                                                                    <Box sx={{ width: "160px", display: 'flex', alignItems: 'center', flexWrap: 'wrap' }} className="timer-height">
                                                                        <Typography variant='body2' fontWeight="500" mb={0.5} color="#9A9AAF" style={{ width: "100%" }}>Close Time</Typography>
                                                                        <TimePickerASAP
                                                                            value={slot?.endTime}
                                                                            onChange={(val) =>
                                                                                handleSlotChange(dayObj?.day, index, "endTime", val)
                                                                            }
                                                                            disableBeforeMinutes={
                                                                                slot.startTime ? parseTimeToMinutes(slot?.startTime) : undefined
                                                                            }
                                                                            error={validationErrors[dayObj.day]?.[index]?.endTime}
                                                                        />
                                                                        {(validationErrors[dayObj.day]?.[index]?.startTime || validationErrors[dayObj.day]?.[index]?.endTime) && (
                                                                            <Box sx={{ mt: 0.5 }}>
                                                                                {/* {validationErrors[dayObj.day]?.[index]?.startTime && (
                                                                                    <Typography variant="caption" color="error" display="block">
                                                                                        {validationErrors[dayObj.day][index].startTime}
                                                                                    </Typography>
                                                                                )} */}
                                                                                {validationErrors[dayObj.day]?.[index]?.endTime && (
                                                                                    <Typography variant="caption" color="error" display="block" fontWeight="normal">
                                                                                        {validationErrors[dayObj.day][index].endTime}
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                </Box>

                                                            </Box>

                                                            {/* Region Overrides Section */}
                                                            <Box className="time-card-rep">
                                                                <Typography variant='h6' fontWeight="semibold" mb={2}>
                                                                    Region Overrides (Optional)
                                                                </Typography>

                                                                <Box className="region-overrides-sec">
                                                                    {dayObj.regionOverrides.map((region, regionIndex) => {
                                                                        const availableRegions = getAvailableRegions();
                                                                        const isAllSelected = region.regionId?.length === regionData.length && regionData.length > 0;
                                                                        const isSelectAllDisabled = availableRegions.length === 0 && !isAllSelected;
                                                                        return (
                                                                            <Box className="region-inner-rep" key={regionIndex}>
                                                                                <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 2 }}>
                                                                                    <Box sx={{ display: 'flex', alignItems: 'start', columnGap: 4, rowGap: 2, flexWrap: 'wrap' }}>
                                                                                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                                                                            <Typography variant='body2' fontWeight="500" mb={0.5} color="#9A9AAF" style={{ width: "100%" }}>Select Region</Typography>
                                                                                            <TextField
                                                                                                select
                                                                                                size="small"
                                                                                                label="Select Region"
                                                                                                value={region.regionId || []}
                                                                                                onChange={(e) => {
                                                                                                    let value = e.target.value;
                                                                                                    // Handle 'select-all' manually
                                                                                                    if (value.includes('select-all')) {
                                                                                                        if ((region.regionId || []).length === regionData.length) {
                                                                                                            // Deselect all if already all selected
                                                                                                            handleRegionOverrideChange(dayObj.day, regionIndex, 'regionId', []);
                                                                                                        } else {
                                                                                                            // Select all
                                                                                                            handleRegionOverrideChange(dayObj.day, regionIndex, 'regionId', regionData.map(reg => reg.id));
                                                                                                        }
                                                                                                    } else {
                                                                                                        handleRegionOverrideChange(dayObj.day, regionIndex, 'regionId', value);
                                                                                                    }
                                                                                                }}
                                                                                                sx={{ minWidth: 200 }}
                                                                                                error={!!validationErrors[dayObj.day]?.regions?.[regionIndex]?.regionId}
                                                                                                helperText={validationErrors[dayObj.day]?.regions?.[regionIndex]?.regionId}
                                                                                                SelectProps={{
                                                                                                    multiple: true,
                                                                                                    renderValue: (selected) => {
                                                                                                        if (selected.length === 0) {
                                                                                                            return "Select Region";
                                                                                                        }
                                                                                                        if (selected.length === regionData.length) {
                                                                                                            return "All Regions";
                                                                                                        }
                                                                                                        return `${selected.length} Region${selected.length !== 1 ? 's' : ''}`;
                                                                                                    }
                                                                                                }}
                                                                                                style={{ width: "100%" }}
                                                                                            >
                                                                                                <MenuItem value="select-all" isabled={isSelectAllDisabled}>
                                                                                                    <Checkbox
                                                                                                        checked={region.regionId?.length === regionData.length && regionData.length > 0}
                                                                                                        indeterminate={
                                                                                                            region.regionId?.length > 0 &&
                                                                                                            region.regionId?.length < regionData.length
                                                                                                        }
                                                                                                        disabled={isSelectAllDisabled}

                                                                                                    />
                                                                                                    <Typography variant="body2" fontWeight="medium" color={isSelectAllDisabled ? "text.disabled" : "text.primary"}>
                                                                                                        Select All Regions
                                                                                                        {isSelectAllDisabled && " (All regions assigned)"}
                                                                                                    </Typography>
                                                                                                </MenuItem>

                                                                                                {regionData.map(reg => (
                                                                                                    <MenuItem key={reg.id} value={reg.id} disabled={isRegionDisabled(reg.id, dayObj.day, regionIndex)}
                                                                                                    >
                                                                                                        <Checkbox checked={region.regionId?.includes(reg.id) || false} disabled={isRegionDisabled(reg.id, dayObj.day, regionIndex)} />
                                                                                                        {reg.name}
                                                                                                    </MenuItem>
                                                                                                ))}
                                                                                            </TextField>
                                                                                        </Box>

                                                                                        <Box sx={{ display: 'flex', alignItems: 'start', flexWrap: 'wrap', gap: '5px' }}>
                                                                                            <Box sx={{ width: "160px", display: 'flex', alignItems: 'center', flexWrap: 'wrap' }} className="timer-large-height">
                                                                                                <Typography variant='body2' fontWeight="500" mb={0.5} color="#9A9AAF" style={{ width: "100%" }}>Open Time</Typography>
                                                                                                <TimePickerASAP
                                                                                                    value={region?.openTime}
                                                                                                    onChange={(val) =>
                                                                                                        handleRegionOverrideChange(dayObj?.day, regionIndex, "openTime", val)
                                                                                                    }
                                                                                                    error={validationErrors[dayObj.day]?.regions?.[regionIndex]?.openTime}
                                                                                                />
                                                                                                {hasRegionError(dayObj.day, regionIndex) && (
                                                                                                    <Box sx={{ mt: 0.5 }}>
                                                                                                        {validationErrors[dayObj.day]?.regions?.[regionIndex]?.openTime && (
                                                                                                            <Typography variant="caption" color="error" display="block" fontWeight="normal">
                                                                                                                {validationErrors[dayObj.day].regions[regionIndex].openTime}
                                                                                                            </Typography>
                                                                                                        )}
                                                                                                        {/* {validationErrors[dayObj.day]?.regions?.[regionIndex]?.closeTime && (
                                                                                                            <Typography variant="caption" color="error" display="block">
                                                                                                                {validationErrors[dayObj.day].regions[regionIndex].closeTime}
                                                                                                            </Typography>
                                                                                                        )} */}
                                                                                                    </Box>
                                                                                                )}
                                                                                            </Box>
                                                                                            <span style={{ position: "relative", top: "32px", color: "#2E2E3A", margin: "0 5px" }}>-</span>
                                                                                            <Box sx={{ width: "160px", display: 'flex', alignItems: 'center', flexWrap: 'wrap' }} className="timer-large-height">
                                                                                                <Typography variant='body2' fontWeight="500" mb={0.5} color="#9A9AAF" style={{ width: "100%" }}>Close Time</Typography>
                                                                                                <TimePickerASAP
                                                                                                    value={region?.closeTime}
                                                                                                    onChange={(val) =>
                                                                                                        handleRegionOverrideChange(dayObj?.day, regionIndex, "closeTime", val)
                                                                                                    }
                                                                                                    disableBeforeMinutes={
                                                                                                        region.openTime ? parseTimeToMinutes(region?.openTime) : undefined
                                                                                                    }
                                                                                                    error={validationErrors[dayObj.day]?.regions?.[regionIndex]?.closeTime}
                                                                                                />
                                                                                                {hasRegionError(dayObj.day, regionIndex) && (
                                                                                                    <Box sx={{ mt: 0.5 }}>
                                                                                                        {/* {validationErrors[dayObj.day]?.regions?.[regionIndex]?.openTime && (
                                                                                                            <Typography variant="caption" color="error" display="block">
                                                                                                                {validationErrors[dayObj.day].regions[regionIndex].openTime}
                                                                                                            </Typography>
                                                                                                        )} */}
                                                                                                        {validationErrors[dayObj.day]?.regions?.[regionIndex]?.closeTime && (
                                                                                                            <Typography variant="caption" color="error" display="block" fontWeight="normal">
                                                                                                                {validationErrors[dayObj.day].regions[regionIndex].closeTime}
                                                                                                            </Typography>
                                                                                                        )}
                                                                                                    </Box>
                                                                                                )}
                                                                                            </Box>
                                                                                        </Box>

                                                                                        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: "8px" }}>
                                                                                            {region.regionId && region.regionId.length > 0
                                                                                                ? region.regionId.map((regionId, idx) => {
                                                                                                    const regionObj = regionData.find(reg => reg.id === regionId);
                                                                                                    return regionObj ? (
                                                                                                        <Typography variant='body2' fontWeight="medium" key={regionId} component="span" sx={{ borderRadius: "6px", background: "#F1F1F1", display: 'inline-flex', alignItems: 'center', padding: "8px 10px", lineHeight: "normal", color: "#2E2E3A", fontSize: "12px" }}>
                                                                                                            {regionObj.name}
                                                                                                            <IconButton
                                                                                                                size="small"
                                                                                                                onClick={(e) => {
                                                                                                                    e.stopPropagation();
                                                                                                                    const updatedRegionIds = region.regionId.filter(id => id !== regionId);
                                                                                                                    handleRegionOverrideChange(dayObj.day, regionIndex, 'regionId', updatedRegionIds);
                                                                                                                }}
                                                                                                                sx={{ ml: 0.5, p: 0 }}
                                                                                                            >
                                                                                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 22 22" fill="none" style={{ background: "#FD4438", borderRadius: "50%", padding: "2px" }}>
                                                                                                                    <path d="M5.5 16.5L16.5 5.5M5.5 5.5L16.5 16.5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                                                                </svg>
                                                                                                            </IconButton>
                                                                                                            {idx < region.regionId.length - 1 ? ' ' : ''}
                                                                                                        </Typography>
                                                                                                    ) : '';
                                                                                                }).filter(Boolean)
                                                                                                : ``
                                                                                            }

                                                                                            {
                                                                                                region.regionId.length > 0 &&
                                                                                                <Typography
                                                                                                    variant="body2"
                                                                                                    color="#2E2E3A"
                                                                                                    fontWeight="semibold"
                                                                                                    fontSize="12px"
                                                                                                    sx={{
                                                                                                        cursor: 'pointer',
                                                                                                        textDecoration: 'underline',
                                                                                                    }}
                                                                                                    onClick={() => handleRegionOverrideChange(dayObj.day, regionIndex, 'regionId', [])}
                                                                                                >
                                                                                                    Clear All
                                                                                                </Typography>
                                                                                            }
                                                                                        </Box>
                                                                                    </Box>

                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            onClick={() => removeRegionOverride(dayObj.day, regionIndex)}
                                                                                            color='primary' variant="primary"
                                                                                        >
                                                                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 22 22" fill="none">
                                                                                                <path d="M5.5 16.5L16.5 5.5M5.5 5.5L16.5 16.5" stroke="#2E2E3A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                                            </svg>
                                                                                        </IconButton>
                                                                                    </Box>
                                                                                </Box>

                                                                                <Box sx={{ display: 'flex', alignItems: 'center', marginTop: "5px" }}>
                                                                                    <Switch
                                                                                        checked={!region.isClosed}
                                                                                        onChange={() => handleRegionClosedToggle(dayObj.day, regionIndex)}
                                                                                    />
                                                                                    <Typography variant='body2' style={{ color: "#9A9AAF", fontWeight: "500", lineHeight: "16px" }}>
                                                                                        {!region.isClosed ? 'Closed Today' : 'Open'}
                                                                                    </Typography>
                                                                                </Box>

                                                                            </Box>
                                                                        )
                                                                    })}
                                                                </Box>


                                                                {
                                                                    !areAllRegionsUsed() &&
                                                                    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', marginTop: "15px" }}>
                                                                        <Button
                                                                            startIcon={<AddRoundedIcon fontSize='large' />}
                                                                            variant='contained'
                                                                            color='primary'
                                                                            size='small'
                                                                            sx={{
                                                                                minWidth: '160px',
                                                                                backgroundColor: '#2E2E3A !important',
                                                                                boxShadow: "none !important",
                                                                                opacity: areAllRegionsUsed() ? 0.6 : 1
                                                                            }}
                                                                            style={{ textTransform: 'none', minHeight: '35px', padding: "8px 20px" }}
                                                                            onClick={() => addRegionOverride(dayObj.day)}
                                                                            disabled={areAllRegionsUsed()}
                                                                        >
                                                                            Add Region
                                                                        </Button>
                                                                    </Box>
                                                                }
                                                            </Box>

                                                        </Box>
                                                    ))}
                                                </>
                                            )}
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>
                            ))}


                        <Grid item xs={12} md={12} lg={12}>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={closeRepeatMenu}
                                PaperProps={{ sx: { p: 1 } }}
                                className="repeat-slot-dropdown"
                            >
                                <Typography variant="body2" fontWeight="semibold" sx={{ px: 1, pb: 1 }} color="#2E2E3A">
                                    Copy Times To...
                                </Typography>
                                {daysOfWeek?.map((day) => {
                                    const isSameDay = day === repeatSourceDay;
                                    const isDisabled = isSameDay || !selectedDaysList.includes(day);

                                    return (
                                        <MenuItem
                                            key={day}
                                            sx={{
                                                py: 0,
                                                color: isDisabled ? "text.disabled" : "text.primary",
                                            }}
                                            onClick={() => {
                                                if (!isDisabled && !isSameDay) {
                                                    handleRepeatDayToggle(day);
                                                }
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        disabled={isDisabled}
                                                        checked={selectedRepeatDays.includes(day)}
                                                        onChange={(e) => {
                                                            if (isSameDay) return;
                                                            handleRepeatDayToggle(day);
                                                        }}
                                                    />
                                                }
                                                label={day}
                                                sx={{
                                                    color: isDisabled ? "text.disabled" : "text.primary",
                                                    pointerEvents: 'none' // Prevent the label from interfering with click
                                                }}
                                            />
                                        </MenuItem>
                                    );
                                })}
                            </Menu>
                        </Grid>
                    </Grid>
                </Card>
            </Box>

            <Box p={{ xs: 0 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={12} lg={12}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} mt={4} mb={4}>
                            <MDButton
                                sx={{ minWidth: '150px' }}
                                type='submit'
                                variant='contained'
                                onClick={handleSubmit}
                                color={'primary'}
                                disabled={isLoading || formData?.days?.length === 0}
                            >
                                {isLoading ? <CircularProgress size={14} justifyContent="center" style={{ color: "#ffffff" }} /> : 'Save'}
                            </MDButton>
                            <MDButton
                                sx={{ minWidth: '150px' }}
                                type='button'
                                variant='outlined'
                                color='dark'
                                onClick={() => navigate('/hyperwolf/asap-time-slot?roleTab=generalTimings')}
                            >
                                Cancel
                            </MDButton>
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    )
}

export default CreateGeneralTiming